/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { saveAs } from "file-saver";

// @mui material hooks
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// @mui material components
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { API_ESA_OFFICE } from "utils";

function PerorangDetailApel() {
  const [detailData, setDetailData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pegawaiInfo, setPegawaiInfo] = useState(null);
  const navigate = useNavigate();
  const { uid_RSS } = useParams();
  const [searchParams] = useSearchParams();

  // Mobile detection
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // State untuk modal detail
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);

  // Ambil query parameters
  const tanggalAwal = searchParams.get("tanggal_awal");
  const tanggalAkhir = searchParams.get("tanggal_akhir");
  const nama = searchParams.get("nama");

  const fetchDetailData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}apel-harian/pegawai/${uid_RSS}`,
        {
          params: {
            tanggal_awal: tanggalAwal,
            tanggal_akhir: tanggalAkhir,
          },
        }
      );

      if (response.data && response.data.length > 0) {
        // Gabungkan data apel pagi dan siang pada tanggal yang sama
        const groupedData = groupApelByDate(response.data);
        setDetailData(groupedData);
        setPegawaiInfo(response.data[0]);
      } else {
        setDetailData([]);
        swal({
          title: "Info",
          text: "Tidak ada data apel harian untuk periode yang dipilih",
          icon: "info",
          button: "OK",
        });
      }
    } catch (error) {
      console.error("Error fetching detail data:", error);
      swal({
        title: "Error!",
        text: "Terjadi kesalahan saat mengambil data detail",
        icon: "error",
        button: "OK",
      });
      setDetailData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fungsi untuk menggabungkan data apel berdasarkan tanggal
  const groupApelByDate = (data) => {
    const grouped = {};

    data.forEach((item) => {
      const tanggal = item.tanggal;
      if (!grouped[tanggal]) {
        grouped[tanggal] = {
          tanggal: tanggal,
          apel_pagi: null,
          apel_siang: null,
        };
      }

      if (item.jenis_apel === "Pagi") {
        grouped[tanggal].apel_pagi = item;
      } else if (item.jenis_apel === "Siang") {
        grouped[tanggal].apel_siang = item;
      }
    });

    // Konversi ke array dan urutkan berdasarkan tanggal
    return Object.values(grouped).sort(
      (a, b) => new Date(a.tanggal) - new Date(b.tanggal)
    );
  };

  useEffect(() => {
    if (uid_RSS && tanggalAwal && tanggalAkhir) {
      fetchDetailData();
    }
  }, [uid_RSS, tanggalAwal, tanggalAkhir]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      weekday: "long",
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return date.toLocaleDateString("id-ID", options);
  };

  const formatTanggal = (dateString) => {
    const date = new Date(dateString);
    const options = {
      day: "numeric",
      month: "short",
      year: "numeric",
    };
    return date.toLocaleDateString("id-ID", options);
  };

  const formatTime = (timeString) => {
    if (!timeString) return "-";
    return timeString.substring(0, 5); // Ambil hanya jam:menit
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Hadir":
        return "success";
      case "Izin":
        return "warning";
      case "Sakit":
        return "info";
      case "Dinas Luar":
        return "primary";
      case "Tanpa Berita":
        return "error";
      default:
        return "default";
    }
  };

  // Fungsi untuk membuka modal detail
  const handleOpenDetailModal = (laporan) => {
    setSelectedDetail(laporan);
    setOpenDetailModal(true);
  };

  // Fungsi untuk menutup modal detail
  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setSelectedDetail(null);
  };

  const columns = [
    {
      Header: "No",
      accessor: "no",
      width: "6%",
      align: "center",
      Cell: ({ row }) => row.index + 1,
    },
    {
      Header: "Tanggal",
      accessor: "tanggal",
      width: "12%",
      Cell: ({ value }) => (
        <MDTypography variant="caption" fontWeight="bold" color="text">
          {formatTanggal(value)}
        </MDTypography>
      ),
    },
    {
      Header: "Hari",
      accessor: "hari",
      width: "10%",
      Cell: ({ row }) => formatDate(row.original.tanggal).split(",")[0],
    },
    {
      Header: "Apel Pagi",
      accessor: "apel_pagi",
      width: "18%",
      Cell: ({ value }) => {
        if (!value) {
          return (
            <MDTypography variant="caption" color="text.secondary">
              Tidak ada data
            </MDTypography>
          );
        }
        return (
          <MDBox>
            <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
              <Chip
                label="Pagi"
                color="warning"
                size="small"
                variant="filled"
              />
              <MDTypography variant="caption" fontWeight="bold" color="text">
                {formatTime(value.jam_absen)}
              </MDTypography>
            </MDBox>
            <Chip
              label={value.status}
              color={getStatusColor(value.status)}
              size="small"
              variant="filled"
            />
          </MDBox>
        );
      },
    },
    {
      Header: "Apel Siang",
      accessor: "apel_siang",
      width: "18%",
      Cell: ({ value }) => {
        if (!value) {
          return (
            <MDTypography variant="caption" color="text.secondary">
              Tidak ada data
            </MDTypography>
          );
        }
        return (
          <MDBox>
            <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
              <Chip label="Siang" color="info" size="small" variant="filled" />
              <MDTypography variant="caption" fontWeight="bold" color="text">
                {formatTime(value.jam_absen)}
              </MDTypography>
            </MDBox>
            <Chip
              label={value.status}
              color={getStatusColor(value.status)}
              size="small"
              variant="filled"
            />
          </MDBox>
        );
      },
    },
    {
      Header: "Keterangan",
      accessor: "keterangan",
      width: "20%",
      Cell: ({ row }) => {
        const pagiKeterangan = row.original.apel_pagi?.keterangan;
        const siangKeterangan = row.original.apel_siang?.keterangan;

        if (!pagiKeterangan && !siangKeterangan) {
          return (
            <MDTypography variant="caption" color="text.secondary">
              -
            </MDTypography>
          );
        }

        return (
          <MDBox>
            {pagiKeterangan && (
              <MDBox mb={0.5}>
                <MDTypography variant="caption" fontWeight="bold" color="text">
                  Pagi:
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block">
                  {pagiKeterangan}
                </MDTypography>
              </MDBox>
            )}
            {siangKeterangan && (
              <MDBox>
                <MDTypography variant="caption" fontWeight="bold" color="text">
                  Siang:
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block">
                  {siangKeterangan}
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        );
      },
    },
  ];

  // Fungsi untuk menghitung statistik
  const calculateStats = () => {
    const stats = {
      totalTanggal: detailData.length,
      totalApel: detailData.reduce((sum, item) => {
        let count = 0;
        if (item.apel_pagi) count++;
        if (item.apel_siang) count++;
        return sum + count;
      }, 0),
      apelPagi: detailData.filter((item) => item.apel_pagi).length,
      apelSiang: detailData.filter((item) => item.apel_siang).length,
      hadir: detailData.reduce((sum, item) => {
        let count = 0;
        if (item.apel_pagi && item.apel_pagi.status === "Hadir") count++;
        if (item.apel_siang && item.apel_siang.status === "Hadir") count++;
        return sum + count;
      }, 0),
      izin: detailData.reduce((sum, item) => {
        let count = 0;
        if (item.apel_pagi && item.apel_pagi.status === "Izin") count++;
        if (item.apel_siang && item.apel_siang.status === "Izin") count++;
        return sum + count;
      }, 0),
      sakit: detailData.reduce((sum, item) => {
        let count = 0;
        if (item.apel_pagi && item.apel_pagi.status === "Sakit") count++;
        if (item.apel_siang && item.apel_siang.status === "Sakit") count++;
        return sum + count;
      }, 0),
      dinasLuar: detailData.reduce((sum, item) => {
        let count = 0;
        if (item.apel_pagi && item.apel_pagi.status === "Dinas Luar") count++;
        if (item.apel_siang && item.apel_siang.status === "Dinas Luar") count++;
        return sum + count;
      }, 0),
      tanpaBerita: detailData.reduce((sum, item) => {
        let count = 0;
        if (item.apel_pagi && item.apel_pagi.status === "Tanpa Berita") count++;
        if (item.apel_siang && item.apel_siang.status === "Tanpa Berita")
          count++;
        return sum + count;
      }, 0),
    };
    return stats;
  };

  const stats = calculateStats();

  const handleExportExcel = () => {
    if (!detailData.length) {
      swal({
        title: "Info",
        text: "Tidak ada data untuk di-export",
        icon: "info",
        button: "OK",
      });
      return;
    }

    // Buat HTML table untuk Excel
    const tableHeaders = [
      "No",
      "Tanggal",
      "Hari",
      "Apel Pagi - Jam",
      "Apel Pagi - Status",
      "Apel Pagi - Keterangan",
      "Apel Siang - Jam",
      "Apel Siang - Status",
      "Apel Siang - Keterangan",
    ];

    let tableRows = "";
    detailData.forEach((row, index) => {
      const hari = formatDate(row.tanggal).split(",")[0];
      tableRows += `
        <tr>
          <td>${index + 1}</td>
          <td>${formatTanggal(row.tanggal)}</td>
          <td>${hari}</td>
          <td>${row.apel_pagi ? formatTime(row.apel_pagi.jam_absen) : "-"}</td>
          <td>${row.apel_pagi ? row.apel_pagi.status : "-"}</td>
          <td>${row.apel_pagi ? row.apel_pagi.keterangan || "-" : "-"}</td>
          <td>${
            row.apel_siang ? formatTime(row.apel_siang.jam_absen) : "-"
          }</td>
          <td>${row.apel_siang ? row.apel_siang.status : "-"}</td>
          <td>${row.apel_siang ? row.apel_siang.keterangan || "-" : "-"}</td>
        </tr>
      `;
    });

    const htmlContent = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8">
          <title>Detail Apel Harian - ${nama} (${tanggalAwal} s/d ${tanggalAkhir})</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
          </style>
        </head>
        <body>
          <h2>Detail Apel Harian Pegawai: ${nama}</h2>
          <h3>Periode: ${formatTanggal(tanggalAwal)} s/d ${formatTanggal(
      tanggalAkhir
    )}</h3>
          <h4>Statistik:</h4>
          <ul>
            <li>Total Tanggal: ${stats.totalTanggal}</li>
            <li>Total Apel: ${stats.totalApel}</li>
            <li>Apel Pagi: ${stats.apelPagi}</li>
            <li>Apel Siang: ${stats.apelSiang}</li>
            <li>Hadir: ${stats.hadir}</li>
            <li>Izin: ${stats.izin}</li>
            <li>Sakit: ${stats.sakit}</li>
            <li>Dinas Luar: ${stats.dinasLuar}</li>
            <li>Tanpa Berita: ${stats.tanpaBerita}</li>
          </ul>
          <table>
            <thead>
              <tr>
                ${tableHeaders.map((header) => `<th>${header}</th>`).join("")}
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const blob = new Blob([htmlContent], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    saveAs(
      blob,
      `detail-apel-harian-${nama}-${tanggalAwal}-${tanggalAkhir}.xls`
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <MDTypography variant="h4">
                Detail Apel Harian - {nama}
              </MDTypography>
            </Grid>
            <Grid item>
              <MDBox display="flex" gap={1}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  onClick={handleExportExcel}
                  disabled={!detailData.length}
                >
                  <Icon>description</Icon>
                  &nbsp;Export Excel
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="info"
                  onClick={() => navigate(-1)}
                >
                  <Icon>arrow_back</Icon>&nbsp;Kembali
                </MDButton>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>

        {/* Statistik */}
        <MDBox mb={3}>
          <MDTypography variant="h6" color="text" mb={2}>
            Statistik Apel Harian
          </MDTypography>
          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Chip
                icon={<Icon>calendar_today</Icon>}
                label={`Total Tanggal: ${stats.totalTanggal}`}
                color="primary"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>schedule</Icon>}
                label={`Total Apel: ${stats.totalApel}`}
                color="secondary"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>wb_sunny</Icon>}
                label={`Apel Pagi: ${stats.apelPagi}`}
                color="warning"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>wb_cloudy</Icon>}
                label={`Apel Siang: ${stats.apelSiang}`}
                color="info"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>check_circle</Icon>}
                label={`Hadir: ${stats.hadir}`}
                color="success"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>warning</Icon>}
                label={`Izin: ${stats.izin}`}
                color="warning"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>local_hospital</Icon>}
                label={`Sakit: ${stats.sakit}`}
                color="info"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>business</Icon>}
                label={`Dinas Luar: ${stats.dinasLuar}`}
                color="primary"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
            <Grid item>
              <Chip
                icon={<Icon>error</Icon>}
                label={`Tanpa Berita: ${stats.tanpaBerita}`}
                color="error"
                variant="filled"
                sx={{ fontSize: "0.875rem", fontWeight: "bold" }}
              />
            </Grid>
          </Grid>
          <MDBox mt={1}>
            <MDTypography variant="caption" color="text.secondary">
              Periode: {formatTanggal(tanggalAwal)} s/d{" "}
              {formatTanggal(tanggalAkhir)}
            </MDTypography>
          </MDBox>
        </MDBox>

        <MDBox mb={2}>
          <MDTypography variant="h6" color="text">
            Data Apel Harian: {formatTanggal(tanggalAwal)} s/d{" "}
            {formatTanggal(tanggalAkhir)}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            Total: {detailData.length} tanggal (Diurutkan berdasarkan tanggal)
          </MDTypography>
        </MDBox>

        {isMobile ? (
          <MDBox display="flex" flexDirection="column" gap={1}>
            {loading && (
              <MDTypography variant="button" color="text.secondary">
                Memuat...
              </MDTypography>
            )}
            {!loading &&
              detailData.map((laporan, index) => (
                <Card key={`${laporan.tanggal || "laporan"}-${index}`}>
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12}>
                        <MDBox
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <MDTypography variant="button" fontWeight="bold">
                            #{index + 1}
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            {formatDate(laporan.tanggal)}
                          </MDTypography>
                        </MDBox>
                      </Grid>

                      {/* Apel Pagi */}
                      <Grid item xs={12}>
                        {laporan.apel_pagi ? (
                          <MDBox>
                            <MDBox
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={0.5}
                            >
                              <Chip
                                label="Pagi"
                                color="warning"
                                size="small"
                                variant="filled"
                              />
                              <MDTypography
                                variant="caption"
                                fontWeight="bold"
                                color="text"
                              >
                                {formatTime(laporan.apel_pagi.jam_absen)}
                              </MDTypography>
                              <Chip
                                label={laporan.apel_pagi.status}
                                color={getStatusColor(laporan.apel_pagi.status)}
                                size="small"
                                variant="filled"
                              />
                            </MDBox>
                            {laporan.apel_pagi.keterangan && (
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                Keterangan: {laporan.apel_pagi.keterangan}
                              </MDTypography>
                            )}
                          </MDBox>
                        ) : (
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            Apel Pagi: Tidak ada data
                          </MDTypography>
                        )}
                      </Grid>

                      {/* Apel Siang */}
                      <Grid item xs={12}>
                        {laporan.apel_siang ? (
                          <MDBox>
                            <MDBox
                              display="flex"
                              alignItems="center"
                              gap={1}
                              mb={0.5}
                            >
                              <Chip
                                label="Siang"
                                color="info"
                                size="small"
                                variant="filled"
                              />
                              <MDTypography
                                variant="caption"
                                fontWeight="bold"
                                color="text"
                              >
                                {formatTime(laporan.apel_siang.jam_absen)}
                              </MDTypography>
                              <Chip
                                label={laporan.apel_siang.status}
                                color={getStatusColor(
                                  laporan.apel_siang.status
                                )}
                                size="small"
                                variant="filled"
                              />
                            </MDBox>
                            {laporan.apel_siang.keterangan && (
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                              >
                                Keterangan: {laporan.apel_siang.keterangan}
                              </MDTypography>
                            )}
                          </MDBox>
                        ) : (
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            Apel Siang: Tidak ada data
                          </MDTypography>
                        )}
                      </Grid>

                      {/* Keterangan Gabungan (konsisten dengan desktop) */}
                      {(laporan.apel_pagi?.keterangan ||
                        laporan.apel_siang?.keterangan) && (
                        <Grid item xs={12}>
                          <MDBox>
                            {laporan.apel_pagi?.keterangan && (
                              <MDBox mb={0.5}>
                                <MDTypography
                                  variant="caption"
                                  fontWeight="bold"
                                  color="text"
                                >
                                  Pagi:
                                </MDTypography>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  display="block"
                                >
                                  {laporan.apel_pagi.keterangan}
                                </MDTypography>
                              </MDBox>
                            )}
                            {laporan.apel_siang?.keterangan && (
                              <MDBox>
                                <MDTypography
                                  variant="caption"
                                  fontWeight="bold"
                                  color="text"
                                >
                                  Siang:
                                </MDTypography>
                                <MDTypography
                                  variant="caption"
                                  color="text"
                                  display="block"
                                >
                                  {laporan.apel_siang.keterangan}
                                </MDTypography>
                              </MDBox>
                            )}
                          </MDBox>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              ))}
          </MDBox>
        ) : (
          <DataTable
            table={{
              columns,
              rows: detailData,
            }}
            entriesPerPage={false}
            showTotalEntries={false}
            isSorted={false}
            noEndBorder
            loading={loading}
          />
        )}

        {detailData.length === 0 && !loading && (
          <MDBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            py={4}
          >
            <Icon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}>
              schedule
            </Icon>
            <MDTypography variant="h6" color="text.secondary">
              Tidak ada data apel harian
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary">
              Coba periksa kembali periode yang dipilih
            </MDTypography>
          </MDBox>
        )}

        {loading && (
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <MDTypography variant="h6" color="text.secondary">
              Memuat data...
            </MDTypography>
          </MDBox>
        )}
      </MDBox>

      {/* Modal Detail Apel dihapus pada mobile; data ditampilkan langsung pada kartu */}
    </DashboardLayout>
  );
}

export default PerorangDetailApel;
